// BHP-1064: invoice read-models over Backend2.0.
//
// - Invoice: item/collection shape from `GET /api/invoices` (InvoiceOutputDto).
// - InvoiceSpecification: `GET /api/invoice_specifications?invoice.uuid=…`.
//
// BHP-1160 removed the InvoiceLine read-model: the detail page shows the PDF
// Exact Online issued instead of a table built from our own rows. The endpoint
// still exists for the admin screen, which will bring its own model.
//
// Replaces Models/InvoiceData, which described the legacy `/api/v1` payload:
// epoch-second timestamps, a nested customer/payment/order object graph and
// inlined invoiceLines. Backend2.0 returns ISO strings, uuids instead of nested
// entities, and the lines as their own collection.

/** Mirrors the status constants on `App\Invoice\Entity\Invoice`. */
export type InvoiceStatus =
  'DRAFT' | 'PENDING' | 'EXPORTING' | 'OPEN' | 'COMPLETE' | 'FAILED';

/**
 * BHP-1160: whether one document of an invoice can be shown.
 *
 * `preparing` is the normal state right after an invoice is finalized — the
 * PDF is fetched from Exact Online by a cron, not at finalize time.
 * `unavailable` means nobody is retrying any more and the customer should ask
 * us rather than keep refreshing. The reason itself stays on the backend
 * (BHP-1144).
 */
export type DocumentState = 'available' | 'preparing' | 'unavailable';

export interface Invoice {
  uuid: string;
  invoiceStatus: InvoiceStatus;
  currency: string | null;
  /** Flat percentage for the whole invoice; there is no per-line VAT. */
  vat: number | null;
  payDate: string | null;
  dueDate: string | null;
  isRecurring: boolean | null;
  description: string | null;
  createdOn: string;
  /**
   * BHP-1110: computed by the backend, never here. Summing the lines in the
   * browser gives a different number than the invoice Exact Online issued —
   * Exact rounds the unit price to two decimals before multiplying, and
   * `totalPrice` on the line does not necessarily follow that rule.
   */
  totalExclVat: number;
  vatAmount: number;
  totalInclVat: number;
  /**
   * BHP-1160: when the invoice became final at Exact Online. The portal only
   * ever receives finalized invoices — drafts are filtered out by the backend —
   * so this is the invoice date to show, not a flag to check.
   */
  finalizedAt: string | null;
  /** State of the invoice PDF itself. */
  documentState: DocumentState;
  /**
   * BHP-1160: Exact Online's own number, as printed inside the PDF and quoted
   * on a bank transfer. Null would mean a finalized invoice without a number,
   * which should not reach a customer.
   */
  invoiceNumber: string | null;
  /**
   * BHP-1174: the Mollie page where this invoice can be paid.
   *
   * Read it as an answer, not as a link. The backend withholds it for an
   * invoice that is already paid, for one whose link is still being created
   * moments after finalizing, and for every invoice at all while payment links
   * are switched off for the environment (BHP-1181). All three are "no pay
   * button", none of them is an error, and none of them is something this
   * side gets to work out for itself.
   *
   * Whether an invoice *has been* paid is a different question, and not one
   * this field answers — that is BHP-1169.
   *
   * **Optional, and that is the wire talking.** "Withheld" means the key is
   * missing, not that it is null: API Platform's SerializerContextBuilder sets
   * `skip_null_values` by default, so a null never leaves the backend. Use
   * {@link paymentLinkOf} rather than comparing this to null — the first
   * version of the pay button compared, got `false` for every invoice, and put
   * an `<a>` with no `href` on every page.
   */
  paymentUrl?: string | null;
  /**
   * BHP-1174: the amount Exact Online issued the invoice for, as a decimal
   * string, and the amount Mollie will ask for. Read it through
   * {@link payableAmountOf} — and never confuse it with `totalInclVat`, which
   * is our own sum and may differ.
   *
   * Optional for the same reason as `paymentUrl`: the API omits null fields.
   */
  amountInclVat?: string | null;
  /**
   * BHP-1169: when the payment settled. Answers "has this been paid" — the
   * other axis from `paymentUrl`'s "can this still be paid". Two questions,
   * two fields; they only merge once `amountOpen` is a real balance
   * (BHP-1168).
   *
   * Optional for the same reason as `paymentUrl`: the API omits null fields,
   * so an unpaid invoice has no key. Read it through {@link paymentStatusOf},
   * never compare this to null.
   */
  paidAt?: string | null;
  /**
   * BHP-1169: what is still owed, as a decimal string. Only the Mollie path
   * writes it today ('0.00' when a payment settles); a missing key means
   * "unknown", not "everything". It becomes a real balance with BHP-1168.
   */
  amountOpen?: string | null;
}

/**
 * The presence rule for optional wire fields, in one place (BHP-1169 review).
 *
 * There are three ways for an optional field to be absent and only one of
 * them looks like it: a missing key (what the API actually sends under
 * skip_null_values), an explicit null (what the type still allows) and an
 * empty string (what a misconfigured value would look like). None of them is
 * a value.
 */
function presentStringOf(value: string | null | undefined): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Decimal strings from the wire, parsed in one place (BHP-1169 review): the
 * backend sends money as decimal strings, and every consumer must agree on
 * what "unparseable" means — null, never NaN and never a half-read prefix.
 */
function decimalOf(value: string | null | undefined): number | null {
  const amount = Number.parseFloat(value ?? '');

  return Number.isFinite(amount) ? amount : null;
}

/**
 * BHP-1174: where this invoice can be paid, or null when it cannot be.
 */
export function paymentLinkOf(invoice: Invoice): string | null {
  return presentStringOf(invoice.paymentUrl);
}

/**
 * BHP-1174: the amount a pay button is allowed to name, or null.
 *
 * Deliberately NOT `totalInclVat`. That one is ours, computed from our own
 * lines, and the backend's InvoiceTotalCalculator ignores the invoice discount
 * by design (BHP-1110) — the moment a discount exists the two numbers part
 * ways. `amountInclVat` is the figure Exact wrote back at finalize: what the
 * PDF says, what the invoice mail quotes, and what the Mollie link is built
 * from. Naming any other number next to a pay button means telling a customer
 * one price and charging another.
 *
 * Null when the backend has no amount for us — a pay button then says "Pay"
 * and nothing more, which is honest, where a wrong number would not be.
 */
export function payableAmountOf(invoice: Invoice): number | null {
  return decimalOf(invoice.amountInclVat);
}

/**
 * BHP-1160: the usage specification belonging to an invoice, one per billing
 * month. A collection of its own rather than a field on the invoice, for the
 * same reason as the lines: an invoice can have more than one.
 */
export interface InvoiceSpecification {
  uuid: string;
  invoiceUuid: string;
  /** `YYYY-MM`. */
  month: string;
  documentState: DocumentState;
  generatedAt: string | null;
}

const currencyFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Money as the customer expects to read it. Falls back to EUR: an invoice
 * without a currency is a data problem, not a reason to show a bare number.
 */
export function formatMoney(
  amount: number | null,
  currency: string | null
): string {
  if (amount === null) {
    return '—';
  }

  const code = currency ?? 'EUR';
  let formatter = currencyFormatters.get(code);

  if (formatter === undefined) {
    formatter = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: code
    });
    currencyFormatters.set(code, formatter);
  }

  return formatter.format(amount);
}

/**
 * Date-only, in the reader's usual notation. Empty dates stay empty.
 *
 * Pinned to the business timezone (BHP-1169 review): a UTC-midnight due date
 * would otherwise render as the previous day for any viewer west of UTC —
 * one day off from the date printed on the PDF.
 */
export function formatDate(value: string | null): string {
  if (value === null || value === '') {
    return '—';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('nl-NL', { timeZone: BUSINESS_TIME_ZONE });
}

/**
 * The timezone the business runs in. Due dates and "paid on" dates are
 * business facts — the same invoice must not be overdue for a customer in
 * Auckland while still due for one in Amsterdam (BHP-1169 review), and the
 * date on the badge must match the date on the PDF regardless of the
 * viewer's device timezone.
 */
const BUSINESS_TIME_ZONE = 'Europe/Amsterdam';

/** YYYY-MM-DD in the business timezone — en-CA is the locale that formats so. */
const businessDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE
});

/** The calendar day a timestamp falls on, in the business timezone. */
function businessDayOf(value: Date): string | null {
  return Number.isNaN(value.getTime()) ? null : businessDayFormat.format(value);
}

/**
 * Overdue starts the day AFTER the due date: an invoice due today is not
 * overdue yet. Calendar days compare lexicographically as YYYY-MM-DD, and
 * both sides are pinned to the business timezone, so the boundary is the
 * same for every viewer.
 */
function isPastDue(dueDate: string, now: Date): boolean {
  const dueDay = businessDayOf(new Date(dueDate));
  const today = businessDayOf(now);

  return dueDay !== null && today !== null && dueDay < today;
}

/**
 * BHP-1169: the one answer a customer wants from an invoice — do I still need
 * to do something? Derived, because neither source field can say it alone:
 * `invoiceStatus` tracks our export chain and never learns about payment, and
 * `paidAt`/`amountOpen` know nothing about the due date.
 *
 * `partiallyPaid` carries `overdue` because a token payment must not silence
 * the alarm: money is still owed, and past the due date that is as urgent as
 * never having paid. NOTE: this state is unreachable with today's backend —
 * the only writer of these fields is the Mollie reconciler, which always
 * settles paidAt together with amountOpen '0.00'. It encodes the contract
 * BHP-1168 is expected to honour; revisit it there.
 */
export type PaymentStatus =
  | { kind: 'paid'; paidAt: string }
  | { kind: 'partiallyPaid'; amountOpen: number; overdue: boolean }
  | { kind: 'due'; dueDate: string | null }
  | { kind: 'overdue'; dueDate: string };

/**
 * BHP-1169: derive the customer-facing payment status.
 *
 * Presence-based on purpose: `paidAt` arrives as a missing key, never as
 * null (skip_null_values — the BHP-1174 lesson), and an unparseable paidAt
 * is treated as absent rather than shown as a confident green "Paid on —".
 * "Partially paid" requires both a recorded payment and at least one cent
 * open: an open amount alone is just an unpaid invoice whose balance happens
 * to be known, and a sub-cent rounding residue is not an open debt.
 *
 * `now` is injectable so tests do not depend on the wall clock.
 */
export function paymentStatusOf(
  invoice: Invoice,
  now: Date = new Date()
): PaymentStatus {
  const paidAt = presentStringOf(invoice.paidAt);
  const dueDate = presentStringOf(invoice.dueDate);

  if (paidAt !== null && !Number.isNaN(new Date(paidAt).getTime())) {
    const amountOpen = decimalOf(invoice.amountOpen);

    if (amountOpen !== null && amountOpen >= 0.01) {
      return {
        kind: 'partiallyPaid',
        amountOpen,
        overdue: dueDate !== null && isPastDue(dueDate, now)
      };
    }

    return { kind: 'paid', paidAt };
  }

  if (dueDate !== null && isPastDue(dueDate, now)) {
    return { kind: 'overdue', dueDate };
  }

  return { kind: 'due', dueDate };
}

/**
 * BHP-1169: what the badge says. A plain function rather than render-inline
 * markup so the wording — the only non-trivial part of the badge — is unit
 * testable next to the derivation it narrates.
 */
export function paymentLabel(
  status: PaymentStatus,
  currency: string | null
): string {
  switch (status.kind) {
    case 'paid':
      return `Paid on ${formatDate(status.paidAt)}`;
    case 'partiallyPaid':
      return `Partially paid — ${formatMoney(status.amountOpen, currency)} open`;
    case 'overdue':
      return `Overdue since ${formatDate(status.dueDate)}`;
    case 'due':
      return status.dueDate === null
        ? 'Due'
        : `Due by ${formatDate(status.dueDate)}`;
  }
}

/**
 * BHP-1169: status colours, now about payment instead of the export chain.
 * Alarm follows money overdue — a partial payment past its due date alarms
 * like an unpaid one, because money is still owed; paid is calm — the
 * opposite of the old chip, where OPEN lit up even when nothing was wrong.
 */
const PAYMENT_TONES: Record<PaymentStatus['kind'], string> = {
  paid: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  partiallyPaid: 'bg-amber-50 text-amber-700 ring-amber-200',
  overdue: 'bg-rose-50 text-rose-700 ring-rose-200',
  due: 'bg-slate-100 text-slate-600 ring-slate-200'
};

export function paymentTone(status: PaymentStatus): string {
  if (status.kind === 'partiallyPaid' && status.overdue) {
    return PAYMENT_TONES.overdue;
  }

  return PAYMENT_TONES[status.kind];
}

/**
 * BHP-1160: the billing month of a specification, as a reader says it.
 * Falls back to the raw value rather than inventing a date we cannot parse.
 */
export function formatMonth(month: string): string {
  const parsed = new Date(`${month}-01T00:00:00`);

  return Number.isNaN(parsed.getTime())
    ? month
    : parsed.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
}
